import React, { useState } from "react";
import {
	Box,
	Button,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	Modal,
	Typography,
	Card,
	CardContent,
	Divider, Alert, Link, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { PreferenceIds, ScreenSize } from "../../utils/constants";
import useSystemHook from "../../hooks/SystemHook";
import { useArgusAuth } from "../../hooks/useArgusAuth";
import { useNavigate } from "react-router-dom";
import {eq, isNil} from "lodash";

const modalStyles = {
	[ScreenSize.Desktop]: { width: "50%", padding: 4, maxHeight: "80vh", overflowY: "auto" },
	[ScreenSize.Tablet]: { width: "80%", padding: 3, maxHeight: "80vh", overflowY: "auto" },
	[ScreenSize.Mobile]: { width: "95%", padding: 2, maxHeight: "80vh", overflowY: "auto" },
};

const SavedVehiclesModal = ({
    open,
    openChooseVehicleModal,
    setClose,
    currentUserVehicleId,
    userVehicles,
    activateUserVehicleAsync,
    removeUserVehicleAsync
}) => {
	const { screenSize } = useSystemHook();
	const { isAuthenticated } = useArgusAuth();
	const navigate = useNavigate();
	const [selectedVehicleId, setSelectedVehicleId] = useState(null);
	const [vehicleToDelete, setVehicleToDelete] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [showConfirmDeleteVehicleModal, setShowConfirmDeleteVehicleModal] = useState(false);
	const menuOpen = Boolean(anchorEl);
	const currentVehicle = userVehicles.find((v) => v.userVehicleId === currentUserVehicleId);

	const gotoLoginPage = () => {
		return navigate("/login", {state: {from: location}});
	};

	const gotoRegisterPage = () => {
		return navigate("/register", {state: {from: location}});
	};

	const handleMenuClick = (event, id) => {
		setAnchorEl(event.currentTarget);
		setSelectedVehicleId(id);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setSelectedVehicleId(null);
	};

	const closeModal = () => setClose();

	const closeDeleteVehicleModal = () => {
		setVehicleToDelete(null);
		setShowConfirmDeleteVehicleModal(false);
	};

	const handleSetActiveVehicle = async () => {
		await activateUserVehicleAsync(selectedVehicleId);
		setAnchorEl(null);
	};

	const handleDeleteVehicleClick = (vehicleId) => {
		const vehicle = userVehicles.find((v) => v.userVehicleId === vehicleId);
		if (!vehicle) return;

		setVehicleToDelete(vehicle);
		setAnchorEl(null);
		setShowConfirmDeleteVehicleModal(true);
	};

	const handleSearchForParts = (vehicleId) => {
		const activeVehicle = userVehicles.find((v) => v.userVehicleId === vehicleId);
		if (!activeVehicle) return;

		const params = new URLSearchParams();
		params.append("make", activeVehicle.makeId);
		!!activeVehicle.modelId ? params.append("model", activeVehicle.modelId) : null;
		!!activeVehicle.variantId ? params.append("variant", activeVehicle.variantId) : null;
		!!activeVehicle.yearId ? params.append("year", activeVehicle.yearId) : null;

		window.location.href = `/products/all?${params.toString()}`;
		closeModal();
	};

	const handleConfirmDeleteVehicleClick = async () => {
		await removeUserVehicleAsync(vehicleToDelete.userVehicleId);
	};

	return (
		<>
			<Modal open={open} onClose={closeModal}>
				<Box
					sx={{
						...modalStyles[screenSize],
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 10,
						pb: 3,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<>
						{!!isAuthenticated &&
							<>
								<Typography variant="h6" fontWeight="bold" sx={{ p: 3, pt: 1 }}>Your Vehicles</Typography>

								{currentUserVehicleId && (
									<Box sx={{ px: 3, pb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
										<Button
											onClick={() => handleSearchForParts(currentUserVehicleId)}
											size="small"
											color="primary"
											type="button"
										>
											Search for {" "}
											{currentVehicle?.make}
											{currentVehicle?.model ? ` ${currentVehicle.model}` : ""}
											{currentVehicle?.variant ? ` ${currentVehicle.variant}` : ""}
											{currentVehicle?.year ? ` ${currentVehicle.year}` : ""}
											{" "} parts
										</Button>
									</Box>
								)}

								<Divider />

								<>
									{userVehicles.length > 0 &&
										<Grid container spacing={1} sx={{ px: 3, py: 2, flexGrow: 1 }}>
											{userVehicles.map((vehicle) => (
												<Grid item xs={12} sm={6} key={vehicle.userVehicleId}>
													<Card
														sx={{
															boxShadow: currentUserVehicleId === vehicle.userVehicleId ? "0px 0px 10px rgba(236, 51, 35, 0.7)" : 2,
															transition: "0.3s",
															height: "100%",
															display: "flex",
															flexDirection: "column",
															position: "relative",
															p: 2,
															border: "1px solid #ccc",
														}}
													>
														<IconButton
															size="small"
															onClick={(event) => handleMenuClick(event, vehicle.userVehicleId)}
															sx={{ position: "absolute", top: 8, right: 8 }}
														>
															<MoreVertIcon />
														</IconButton>
														<CardContent sx={{ textAlign: "center", mt: 2 }}>
															<Typography variant="subtitle2" fontWeight="bold">
																{vehicle.make} {vehicle.model}
															</Typography>
															<Typography variant="caption" color="textSecondary">
																{vehicle.variant} - {vehicle.year}
															</Typography>
														</CardContent>
													</Card>
												</Grid>
											))}
										</Grid>
									}
									{userVehicles.length <= 0 &&
										<Box textAlign="center" sx={{ mt: 4 }}>
											<Typography variant="body2" color="textSecondary">
												No vehicles saved. Add one now!
											</Typography>
										</Box>
									}
								</>

								<Divider sx={{ mt: "auto" }} />

								<Box sx={{ px: 3, py: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
									<Button
										variant="contained"
										onClick={openChooseVehicleModal}
										size="small"
										color="error"
										type="button"
									>
										Add Another Vehicle
									</Button>
								</Box>

								<Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
                                    {!eq(currentUserVehicleId, selectedVehicleId) && !isNil(selectedVehicleId) &&
                                        <MenuItem onClick={handleSetActiveVehicle}>
                                            <Typography variant="body2">Set as Active</Typography>
                                        </MenuItem>
                                    }
									<MenuItem onClick={() => handleSearchForParts(selectedVehicleId)}>
										<Typography variant="body2">Search for Parts</Typography>
									</MenuItem>
									<MenuItem onClick={() => handleDeleteVehicleClick(selectedVehicleId)}>
										<Typography variant="body2">Delete</Typography>
									</MenuItem>
								</Menu>
							</>
						}
						{!isAuthenticated &&
							<Grid container justifyContent="center" sx={{ mt: 2 }}>
								<Alert severity="warning">
									Please <Link href="/login">log in</Link> or <Link href="/register">create an account</Link> to access this feature.
								</Alert>
							</Grid>
						}
					</>
				</Box>
			</Modal>
			{!!vehicleToDelete &&
				<Dialog open={!!showConfirmDeleteVehicleModal} onClose={closeDeleteVehicleModal}>
					<DialogTitle>Delete Vehicle</DialogTitle>
					<DialogContent>
						<Typography variant="body2">
							Are you sure you want to delete this vehicle from your garage?
						</Typography>
						<br />
						<Box sx={{ textAlign: "center", bgcolor: "#f5f5f5", p: 2, borderRadius: 2, width: "100%" }}>
							<Typography variant="caption"><strong>Make:</strong> {vehicleToDelete.make}</Typography><br/>
							{!!vehicleToDelete.model && <><Typography variant="caption"><strong>Model:</strong> {vehicleToDelete.model}</Typography><br/></>}
							{!!vehicleToDelete.variant && <><Typography variant="caption"><strong>Variant:</strong> {vehicleToDelete.variant}</Typography><br/></>}
							{!!vehicleToDelete.year && <Typography variant="caption"><strong>Year:</strong> {vehicleToDelete.year}</Typography>}
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={closeDeleteVehicleModal} color="error">
							Cancel
						</Button>
						<Button onClick={handleConfirmDeleteVehicleClick} color="primary">
							Confirm
						</Button>
					</DialogActions>
				</Dialog>
			}
		</>

	);
};

export default SavedVehiclesModal;
